import { useState, useEffect, useCallback } from 'react'
import { getBNBExchangeRate, type ExchangeRateResponse } from './api'

interface ExchangeRateStore {
  rate: number | null
  loading: boolean
  error: string | null
  lastUpdated: string | null
  updateRate: () => Promise<void>
  calculateUSD: (bnbAmount: number) => string
}

// 全局汇率状态
let globalExchangeRate: ExchangeRateResponse | null = null
let globalError: string | null = null
let globalLoading = false

// 订阅者列表
const subscribers = new Set<() => void>()

function notifySubscribers() {
  subscribers.forEach(callback => callback())
}

// 获取汇率的核心函数
async function fetchExchangeRate(): Promise<void> {
  if (globalLoading) return
  
  globalLoading = true
  globalError = null
  notifySubscribers()

  try {
    const response = await getBNBExchangeRate(1)
    globalExchangeRate = response
    globalError = null
    console.log('汇率更新成功:', response.exchange_rate)
  } catch (error) {
    globalError = error instanceof Error ? error.message : 'Failed to fetch exchange rate'
    console.error('汇率获取失败:', error)
  } finally {
    globalLoading = false
    notifySubscribers()
  }
}

// 初始化汇率（应用启动时调用）
export function initializeExchangeRate(): void {
  if (!globalExchangeRate && !globalLoading) {
    fetchExchangeRate()
  }
}

// 汇率 hook
export function useExchangeRate(): ExchangeRateStore {
  const [, forceUpdate] = useState({})

  const triggerUpdate = useCallback(() => {
    forceUpdate({})
  }, [])

  useEffect(() => {
    subscribers.add(triggerUpdate)
    return () => {
      subscribers.delete(triggerUpdate)
    }
  }, [triggerUpdate])

  const updateRate = useCallback(async () => {
    await fetchExchangeRate()
  }, [])

  const calculateUSD = useCallback((bnbAmount: number): string => {
    if (!globalExchangeRate) return '0.00'
    return (bnbAmount * globalExchangeRate.exchange_rate).toFixed(2)
  }, [])

  return {
    rate: globalExchangeRate?.exchange_rate || null,
    loading: globalLoading,
    error: globalError,
    lastUpdated: globalExchangeRate?.updated_at || null,
    updateRate,
    calculateUSD
  }
} 